import { CheckCircle2, FileText, RotateCw, ArrowRight, BarChart3 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useCitAccrualForPeriod } from '../../../hooks/useCitAccruals'
import { useCurrency } from '../../../hooks/useCurrency'
import { endOfMonthIso } from '../../../lib/citComputation'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

interface Props {
  year: number
  month: number
  onRunAnother: () => void
}

export function CitSuccessStep({ year, month, onRunAnother }: Props) {
  const { format } = useCurrency()
  const periodEndIso = endOfMonthIso(year, month)
  const { data: accrual, isLoading } = useCitAccrualForPeriod(periodEndIso)
  const periodLabel = `${MONTH_NAMES[month - 1]} ${year}`

  return (
    <div className="max-w-3xl mx-auto py-4 sm:py-8 space-y-6">
      <header className="text-center space-y-3">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-100">
          <CheckCircle2 className="h-8 w-8 text-brand-500" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">CIT accrued!</h1>
          <p className="text-sm text-slate-500 mt-1">
            {periodLabel} CIT accrual is now in the books.
          </p>
        </div>
      </header>

      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
          <Stat label="Period" value={periodLabel} />
          <Stat
            label="This period's charge"
            value={isLoading ? '…' : format(accrual?.periodCharge ?? 0)}
            highlight
          />
          <Stat
            label="Total YTD provision"
            value={isLoading ? '…' : format(accrual?.totalTaxCharge ?? 0)}
          />
        </div>

        {accrual?.journalEntryNumber ? (
          <div className="border-t border-slate-100 px-4 sm:px-6 py-3 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Journal entry
              </p>
              <p className="text-sm font-medium text-slate-900 tabular-nums truncate">
                {accrual.journalEntryNumber}
              </p>
            </div>
            <Link
              to={`/accounting/journal?search=${encodeURIComponent(accrual.journalEntryNumber)}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              <FileText className="h-4 w-4" />
              View journal entry
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        ) : (
          !isLoading && (accrual?.periodCharge ?? 0) === 0 && (
            <div className="border-t border-slate-100 px-4 sm:px-6 py-3 bg-slate-50">
              <p className="text-xs text-slate-600">
                No journal entry was needed for this period — the period delta
                was zero, so the accrual was recorded without a GL movement.
              </p>
            </div>
          )
        )}
      </section>

      <div className="flex flex-col sm:flex-row gap-2 justify-center">
        <Link to="/reports/financial-statements" className="btn-secondary justify-center">
          <BarChart3 className="h-4 w-4" />
          View income statement
        </Link>
        <button
          type="button"
          onClick={onRunAnother}
          className="btn-primary justify-center"
        >
          <RotateCw className="h-4 w-4" />
          Run another period
        </button>
      </div>
    </div>
  )
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="px-4 sm:px-6 py-4">
      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </p>
      <p
        className={`mt-1 tabular-nums ${
          highlight
            ? 'text-base font-semibold text-brand-700'
            : 'text-sm font-medium text-slate-700'
        }`}
      >
        {value}
      </p>
    </div>
  )
}
